import {
  hasWebkitMessageHandler,
  postWebkitMessageWithReply,
} from './webkit';

export const NATIVE_PERMISSION_NAMES = [
  'tracking',
  'contacts',
  'locationWhenInUse',
  'notifications',
] as const;

export type NativePermissionName = (typeof NATIVE_PERMISSION_NAMES)[number];

export const NATIVE_PERMISSION_STATUSES = [
  'notDetermined',
  'authorized',
  'denied',
  'restricted',
  'limited',
  'provisional',
  'unavailable',
] as const;

export type NativePermissionStatus =
  (typeof NATIVE_PERMISSION_STATUSES)[number];

export interface NativePermissionState {
  status: NativePermissionStatus;
  isGranted: boolean;
  canRequest: boolean;
  canOpenSettings: boolean;
}

export interface NativePermissionSnapshot {
  version: 1;
  permissions: Record<NativePermissionName, NativePermissionState>;
  advertisingIdentifiers?: {
    idfa?: string;
  };
}

const REQUIRED_HANDLERS = [
  'getAppPermissions',
  'requestAppPermission',
  'openAppSettings',
] as const;
const APP_STORE_REVIEW_HANDLER = 'requestAppStoreReview';

const permissionNameSet = new Set<string>(NATIVE_PERMISSION_NAMES);
const permissionStatusSet = new Set<string>(NATIVE_PERMISSION_STATUSES);
const zeroUuid = '00000000-0000-0000-0000-000000000000';
const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isValidIdfa = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.toLowerCase() !== zeroUuid &&
  uuidPattern.test(value);

const parsePermissionState = (
  value: unknown,
): NativePermissionState | null => {
  if (!isRecord(value)) return null;
  const { status, isGranted, canRequest, canOpenSettings } = value;
  if (
    typeof status !== 'string' ||
    !permissionStatusSet.has(status) ||
    typeof isGranted !== 'boolean' ||
    typeof canRequest !== 'boolean' ||
    typeof canOpenSettings !== 'boolean'
  ) {
    return null;
  }

  return {
    status: status as NativePermissionStatus,
    isGranted,
    canRequest,
    canOpenSettings,
  };
};

export const parseNativePermissionSnapshot = (
  value: unknown,
): NativePermissionSnapshot | null => {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.permissions)) {
    return null;
  }

  const permissions = {} as Record<
    NativePermissionName,
    NativePermissionState
  >;
  for (const permission of NATIVE_PERMISSION_NAMES) {
    const state = parsePermissionState(value.permissions[permission]);
    if (!state) return null;
    permissions[permission] = state;
  }

  const snapshot: NativePermissionSnapshot = {
    version: 1,
    permissions,
  };
  if (isRecord(value.advertisingIdentifiers)) {
    const { idfa } = value.advertisingIdentifiers;
    if (isValidIdfa(idfa)) {
      snapshot.advertisingIdentifiers = { idfa };
    }
  }
  return snapshot;
};

export const hasNativePermissionBridge = (): boolean =>
  REQUIRED_HANDLERS.every(hasWebkitMessageHandler);

export const getNativePermissionSnapshot =
  async (): Promise<NativePermissionSnapshot | null> => {
    if (!hasNativePermissionBridge()) return null;
    const reply = await postWebkitMessageWithReply(
      'getAppPermissions',
      {},
    );
    return parseNativePermissionSnapshot(reply);
  };

export const requestNativePermission = async (
  permission: NativePermissionName,
): Promise<NativePermissionSnapshot | null> => {
  if (!permissionNameSet.has(permission) || !hasNativePermissionBridge()) {
    return null;
  }
  const reply = await postWebkitMessageWithReply(
    'requestAppPermission',
    { permission },
  );
  return parseNativePermissionSnapshot(reply);
};

export const openNativeAppSettings = async (): Promise<boolean> => {
  if (!hasNativePermissionBridge()) return false;
  const reply = await postWebkitMessageWithReply(
    'openAppSettings',
    {},
  );
  return isRecord(reply) && reply.opened === true;
};

export const hasNativeAppStoreReviewBridge = (): boolean =>
  hasWebkitMessageHandler(APP_STORE_REVIEW_HANDLER);

export const parseNativeAppStoreReviewResponse = (value: unknown): boolean =>
  isRecord(value) && value.requested === true;

/**
 * Asks the native app to submit a StoreKit review request. A true result means
 * the request reached StoreKit; iOS still decides whether to show the prompt.
 */
export const requestNativeAppStoreReview = async (): Promise<boolean> => {
  if (!hasNativeAppStoreReviewBridge()) return false;
  const reply = await postWebkitMessageWithReply(
    APP_STORE_REVIEW_HANDLER,
    {},
  );
  return parseNativeAppStoreReviewResponse(reply);
};

export const getTrackingIdfa = (
  snapshot: NativePermissionSnapshot,
): string | null => {
  if (!snapshot.permissions.tracking.isGranted) return null;
  const idfa = snapshot.advertisingIdentifiers?.idfa;
  return isValidIdfa(idfa) ? idfa : null;
};
